import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from 'components/atoms/Button';
import printerIcon from '../../../assets/svg/printer.svg';
import copyIcon from '../../../assets/svg/copy.svg';
import mastercard from '../../../assets/svg/mastercard.svg';
import nigeriaIcon from '../../../assets/svg/nigeria_flag.svg';
import PageTitleHeader from 'components/molecules/PageTitleHeader';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import ColorTag from 'components/atoms/ColorTag';
import CopyText from 'components/atoms/CopyText';
import { printContent, printStyled } from '../../../components/atoms/PrintFunction';
import { handleDownload } from '../../../components/atoms/DownloadFunction';
import { handleException } from 'utils';
import axios from 'services/axios';
import { BASE_URL, getRequest, loggedUser_baseURL } from 'services/http';
import { connect, useSelector } from 'react-redux';
import Alert from 'components/atoms/Alert';
import Spinner from 'components/atoms/Spinner';
import { useReactToPrint } from 'react-to-print';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { ToTitleCase } from 'components/atoms/CaseManager';
import SeerbitLogo from '../../../assets/images/SeerbitLogoTyped.png';

const TransactionOverview = ({ bearerToken, publicKey, businessId }) => {
    const [loading, setLoading] = useState(false);
    const [errorState, setErrorState] = useState(false);
    const [transaction, setTransaction] = useState<any>([]);
    const useNewRefEle = useRef<HTMLDivElement>(null);

    const location = useLocation();
    const navigate = useNavigate();

    const transactionData = location.state.transactionData;

    useEffect(() => {
        if (transactionData === null) {
            navigate(-1);
        }
    }, []);

    const handlePrint = useReactToPrint({
        content: () => useNewRefEle.current,
    });

    const handleDownloadPdf = () => {
        const input = useNewRefEle.current;
        if (input) {
            html2canvas(input)
                .then((canvas) => {
                    const imgData = canvas.toDataURL('image/png');
                    const pdf = new jsPDF();
                    const imgProps = pdf.getImageProperties(imgData);
                    const pdfWidth = pdf.internal.pageSize.getWidth();
                    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
                    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
                    pdf.save('download.pdf');
                })
                .catch((error) => {
                    console.error('Could not generate PDF', error);
                });
        }
    };

    const countries = useSelector((state: any) => state.app.countries);
    const getCountry = countries.flatMap(country => country.currencies.filter(item => item.code === transactionData.currency));

    const elementId = document.getElementById('printable_content');

    const multipleBtnData = [
        { btnText: "Print Receipt", btnStyle: "bg-SecondaryAccent2 flex-row-reverse px-4 py-2 font-semibold desktop:text-sm mobile:text-xs text-DarkBg3 truncate flex-shrink-0", btnImg: printerIcon, btnImgStyle: "w-4 h-4", onClick: handlePrint },
        { btnText: "Download Receipt", btnStyle: "bg-DarkBg3 px-10 py-2 font-semibold desktop:text-sm mobile:text-xs text-white flex-shrink-0", btnImg: "", btnImgStyle: "", onClick: handleDownloadPdf }
    ]

    return (
        <div className='py-5'>
            <PageTitleHeader
                title="Transaction Overview"
                btn_num={2}
                btnData={multipleBtnData}
                layout='desktop:max-w-[1142px]'
            />

            <div>
                {loading
                    ? <div className={`absolute z-20 flex items-center py-10 justify-center w-full h-[90vh]`}>
                        <Spinner
                            text="Loading..."
                        />
                    </div>

                    : <div ref={useNewRefEle} id="printable_content" className='flex flex-col border rounded-md desktop:max-w-[1142px] mobile:w-full p-16 mobile:p-4'>
                        <div className='bottom-0 flex justify-start text-white mb-8'>
                            <img src={SeerbitLogo} alt='SeerbitLogo' className='w-[150px] h-auto' />
                        </div>
                        <span className='flex mobile:flex-col tablet:flex-col justify-between gap-5 py-5'>
                            <div className='desktop:w-1/2 w-full flex justify-start mobile:justify-center'>
                                <div className='w-full'>
                                    <div>
                                        <span className='text-base leading-7 text-SecondaryAccent'>Amount</span>
                                    </div>

                                    <div className='flex gap-5 items-center'>
                                        <span className='font-bold desktop:text-2xl tablet:text-xl mobile:text-lg leading-7 text-DarkBg3'>{transactionData.currency + ' ' + Intl.NumberFormat().format(transactionData.amount)}</span>
                                        <span className={`${transactionData.transactionStatus === 'COMPLETED' ? 'bg-[#04A7781a] text-Success2' : transactionData.transactionStatus === 'FAILED' ? 'bg-DangerAccent5 text-Danger4' : transactionData.transactionStatus === 'NOT_STARTED' ? 'bg-Yellow_Accent text-Yellow' : 'bg-SecondaryAccent11 text-Accent_blue6'} px-[10px] py-[5px] rounded-md`}>
                                            {transactionData.transactionStatus === 'COMPLETED'
                                                ? 'Success'
                                                : transactionData.transactionStatus === 'FAILED'
                                                    ? 'Failed'
                                                    : transactionData.transactionStatus === 'NOT_STARTED'
                                                        ? 'Pending'
                                                        : 'Expired'
                                            }
                                        </span>
                                    </div>

                                    <div className='mb-12 mt-8 flex flex-col gap-3'>
                                        <span className='text-[15px] font-normal leading-7 text-DarkBg3'>{moment(transactionData.date).format("MMM DD, YYYY - LTS")}</span>
                                        <CopyText
                                            text={transactionData.paymentReference}
                                            textStyle="bg-SecondaryAccent3 px-3 py-1 rounded-md w-fit font-normal text-xs leading-5 text-DarkBg"
                                            mssgStyle='-right-[60px]'
                                            imgStyle="w-4 h-4"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='desktop:w-1/2 w-full flex justify-end mobile:justify-center pt-8'>
                                <div className='flex flex-col w-full'>
                                    <span className='font-bold text-lg leading-[21.6px] mb-5'>Customer Information</span>
                                    <div className='flex flex-col gap-5 border-t py-5'>
                                        <span className='w-auto mobile:w-full flex justify-between items-center'>
                                            <p className='font-normal text-xs leading-[14.4px] text-Black5'>Customer Name</p>
                                            <p className='font-semibold text-sm leading-[16.8px] text-DarkBg8'>{transactionData.customerName}</p>
                                        </span>
                                        <span className='w-auto mobile:w-full flex justify-between items-center'>
                                            <p className='font-normal text-xs leading-[14.4px] text-Black5'>Email</p>
                                            <p className='font-semibold text-sm leading-[16.8px] text-Accent_blue'>{transactionData.senderEmail}</p>
                                        </span>
                                        <span className='w-auto mobile:w-full flex justify-between items-center'>
                                            <p className='font-normal text-xs leading-[14.4px] text-Black5'>Payment Channel</p>
                                            <p className='font-semibold text-sm leading-[16.8px] text-DarkBg8'>{transactionData.paymentChannel}</p>
                                        </span>
                                        <span className='w-auto mobile:w-full flex justify-between items-center'>
                                            <p className='font-normal text-xs leading-[14.4px] text-Black5'>Country</p>
                                            <p className='font-semibold text-sm leading-[16.8px] text-DarkBg8'>{getCountry[0].country}</p>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </span>
                        <span className=''>
                            <div className='border-b py-5'>
                                <span className='font-semibold text-lg leading-[21.6px] text-black'>Payment Breakdown </span>
                            </div>
                            <div className='py-5 flex flex-col gap-6'>
                                <span className='desktop:w-[282px] mobile:w-full flex justify-between items-center'>
                                    <p className='font-normal text-xs leading-[14.4px] text-Black5'>Transaction Amount</p>
                                    <p className='font-semibold text-sm leading-[16.8px] text-DarkBg8'>{transactionData.currency + ' ' + transactionData.amount}</p>
                                </span>
                                <span className='desktop:w-[282px] mobile:w-full flex justify-between items-center'>
                                    <p className='font-normal text-xs leading-[14.4px] text-Black5'>Transaction Fee</p>
                                    <p className='font-semibold text-sm leading-[16.8px] text-DarkBg8'>{transactionData.currency + ' ' + transactionData.fee}</p>
                                </span>
                            </div>
                        </span>
                        <span className=''>
                            <div className='border-b py-5'>
                                <span className='font-semibold text-lg leading-[21.6px] text-black'>Analysis </span>
                            </div>
                            <div className='py-5 flex flex-col gap-6'>
                                <span className='desktop:w-[282px] mobile:w-full flex justify-between items-center'>
                                    <p className='font-normal text-xs leading-[14.4px] text-Black5'>{transactionData.channelType}</p>
                                    <span className='font-semibold text-sm leading-[16.8px] text-DarkBg8 flex gap-2 items-center'>
                                        <img src={mastercard} alt="mastercard" className='w-4 h-4' />
                                        {transactionData.maskNumber}
                                    </span>
                                </span>
                                {/* <span className='desktop:w-[282px] mobile:w-full flex justify-between items-center'>
                                    <p className='font-normal text-xs leading-[14.4px] text-Black5'>Bank Name</p>
                                    <span className='font-semibold text-sm leading-[16.8px] text-DarkBg8 flex gap-2 items-center'> First Bank Nigeria</span>
                                </span> */}
                                <span className='desktop:w-[282px] mobile:w-full flex justify-between items-center'>
                                    <p className='font-normal text-xs leading-[14.4px] text-Black5'>Country</p>
                                    <span className='font-semibold text-sm leading-[16.8px] text-DarkBg8 flex gap-2 items-center'> <img src={nigeriaIcon} alt="mastercard" className='w-4 h-4' /> {getCountry[0].country}</span>
                                </span>
                                <span className='desktop:w-[282px] mobile:w-full flex justify-between items-center'>
                                    <p className='font-normal text-xs leading-[14.4px] text-Black5'>Device Type</p>
                                    <span className='font-semibold text-sm leading-[16.8px] text-DarkBg8 flex gap-2 items-center'> {transactionData.deviceType} </span>
                                </span>
                                <span className='desktop:w-[282px] mobile:w-full flex justify-between items-center'>
                                    <p className='font-normal text-xs leading-[14.4px] text-Black5'>IP Address</p>
                                    <span className='font-semibold text-sm leading-[16.8px] text-DarkBg8 flex gap-2 items-center'> {transactionData.ipAddress} </span>
                                </span>
                            </div>
                        </span>
                    </div>
                }
            </div>
        </div>
    )
}

const mapStateToProps = (state) => ({
    bearerToken: state.auth.user_authData.token.token,
    businessId: state.auth.user_authData.subPartnerId,
    publicKey: state.auth.user_authData.live_public_key
})

export default connect(mapStateToProps)(TransactionOverview);